import { Clock } from 'lucide-react';
import type { TriageDensity } from '../../hooks/useTriageDensity';
import type { EmailThreadMetadata, LabelCategoryMapping, TriageCategory } from '../../types/email';
import { TriageEmptyState } from './TriageEmptyState';
import { TriageThreadItem, triageListClassName } from './TriageThreadItem';

interface TriagePriorityViewProps {
  threads: EmailThreadMetadata[];
  isLoading: boolean;
  density?: TriageDensity;
  labelMap?: Record<string, string>;
  mappings?: LabelCategoryMapping[];
  categories?: TriageCategory[];
  workspaceMembers?: Array<{ id: number; email: string; fullName: string | null }>;
  onArchive: (threadId: string) => void;
  onDelegate: (threadId: string) => void;
  onCreateTask: (threadId: string) => void;
  onTaskClick: (taskId: number) => void;
  onCardClick: (threadId: string) => void;
  /** Re-run analysis for one thread; shown only when the failure is retryable. */
  onRetryAnalysis?: (threadId: string) => void;
  isReadOnly?: boolean;
}

export function TriagePriorityView({
  threads,
  isLoading,
  density,
  labelMap,
  mappings,
  categories,
  workspaceMembers,
  onArchive,
  onDelegate,
  onCreateTask,
  onTaskClick,
  onCardClick,
  onRetryAnalysis,
  isReadOnly,
}: TriagePriorityViewProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Clock className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (threads.length === 0) {
    return <TriageEmptyState />;
  }

  return (
    <div className={triageListClassName(density)}>
      {threads.map((thread) => (
        <TriageThreadItem
          key={thread.threadId}
          thread={thread}
          density={density}
          labelMap={labelMap}
          mappings={mappings}
          categories={categories}
          workspaceMembers={workspaceMembers}
          onArchive={onArchive}
          onDelegate={onDelegate}
          onCreateTask={onCreateTask}
          onTaskClick={onTaskClick}
          onCardClick={onCardClick}
          onRetryAnalysis={onRetryAnalysis}
          isReadOnly={isReadOnly}
        />
      ))}
    </div>
  );
}
